<template>
    <div class="mc" :id="menuId || null" :class="[extraClass, { 'mc--open': open }]" @keydown="onKeyPress" @mouseleave="hideCart" @mouseenter="clearTimeout">
        <span class="visually-hidden" aria-live="polite" ref="removeAnnouncement"></span>

        <div v-if="!loaded" class="mc-loader">
            <div class="circle-spin-2"></div> Loading Cart
        </div>

        <div class="mc-empty"  v-if="loaded && cartQty == 0">
            <strong>Your Cart Is Empty</strong>
            <p><a href="/whats-new/" class="mc-empty__link">See what's new</a> at Sweetwater.</p>
        </div>

        <div v-if="cartitems.length > 0 && loaded">
            <div class="mc-head">
                <div class="mc-title">
                <span class="visually-hidden">{{cartQty}} items in your shopping cart</span>
                    My Cart
                    <span v-if="cartQty < 999" class="mc-qty">{{cartQty}}</span>
                    <span v-else-if="cartQty < 0" class="mc-qty">0</span>
                    <span v-else class="mc-qty">999+</span>
                </div>
                <div class="mc-total"><b>Subtotal:</b> {{formatPrice(cartTotal)}}</div>
            </div>

            <div class="mc-barloader">
                <div class="mc-barloader__progress"></div>
            </div>

            <div class="mc-items">

                <div v-for="item in cartitems.slice().reverse()" v-bind:key="item.itemid">

                    <!-- DONATION PRESENTATION -->
                    <div v-if="item.itemid == 'LKRDonation' && checkExclusions(item.itemid)" :id="'mc-'+item.itemid" class="mc-item">
                        <div class="mc-item__left">
                            <a v-if="item.image" :href="checkUrl(item.itemid, item.serial, item.detail_page)" class="mc-item__img-link"><img :src="item.image" class="mc-item__img" alt="Little Kids Rock" /></a>
                            <div class="mc-item__name">
                                <a :href="checkUrl(item.itemid, item.serial, item.detail_page)" >Little Kids Rock Donation</a>
                                <div class="edit-in-cart">Edit in <a href="/store/cart.php">Cart</a></div>
                            </div>
                        </div>
                        <div class="mc-item__right">
                            <div v-if="item.price" class="mc-item__price">
                                {{formatPrice((item.qty / 100))}}
                            </div>
                            <div class="mc-item__nodelete"></div>
                        </div>
                    </div>

                    <!-- COUPON / SPECIAL OFFER PRESENTATION -->
                    <div v-else-if="item.serial == 'auto_coupon' || item.serial == 'custom_bundle' || (item.serial && item.serial.indexOf('custom_bundle_coupon:') === 0)" class="mc-item mc-specialoffer">
                        <div class="mc-item__left">
                            <a :href="checkUrl(item.itemid, item.serial, item.detail_page)" class="mc-item__name">Special Offer!</a>
                        </div>
                        <div class="mc-item__right">
                            <div v-if="item.price" class="mc-item__price">
                                {{formatPrice((item.price))}}
                            </div>
                            <div class="mc-item__nodelete"></div>
                        </div>
                    </div>

                    <!-- NORMAL ITEMS -->
                    <div v-else-if="checkExclusions(item.itemid)" :id="'mc-'+item.itemid" class="mc-item">
                        <div class="mc-item__left">
                            <a :href="checkUrl(item.itemid, item.serial, item.detail_page)" class="mc-item__img-link"><img :src="checkImg(item.image, item.itemid)" class="mc-item__img" :alt="'Image of '+item.productname" /></a>

                            <span v-if="item.qty > 999" class="mc-item__qty" aria-label="999+ of this item in your cart">999+</span>
                            <span v-else-if="item.qty > 1" class="mc-item__qty" :aria-label="item.qty+' of this item in your cart'">

                                {{ item.qty }}x
                            </span>
                            <div class="mc-item__name">
                                <a :href="checkUrl(item.itemid, item.serial, item.detail_page)">{{ checkName(item.productname, item.itemid) }}</a>
                                <div v-if="item.serial.includes('parentLineId')" class="edit-in-cart">Edit in <a href="/store/cart.php">Cart</a></div>
                                <br>
                                <a :href="checkUrl(item.itemid, item.serial, item.detail_page)" v-if="item.child_items" class="mc-item__children">
                                    <div v-for="(child, i) in item.child_items" v-if="i < maxChildItemsToShow" :key="child.itemid" class="mc-item__child-image-wrap">
                                        <img class="mc-item__child-image" :src="child.image" :alt="child.productname">
                                    </div>
                                    <div v-if="item.child_items.length > maxChildItemsToShow" class="mc-item__children-plus-more mc-item__child-image-wrap">
                                        <span>
                                            + {{ item.child_items.length - maxChildItemsToShow }}
                                        </span>
                                    </div>
                                </a>
                            </div>
                        </div>
                        <div class="mc-item__right">
                            <div v-if="item.price || item.price === 0" class="mc-item__price">
                                <div v-if="item.original_price > item.price"><strike><i>{{formatPrice(item.original_price)}}</i></strike></div>
                                {{formatPrice(item.price)}}
                                <span v-if="item.qty > 1"><small>each</small></span>
                            </div>
                            <a v-if="!item.serial.includes('parentLineId')" href="#" :data-id="item.itemid" class="mc-item__remove" :aria-label="'Click to remove '+ item.productname + ' from your cart.'">
                                <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" fill="none"><path stroke="#807F7E" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="m1 1 8 8M9 1 1 9"/></svg>
                            </a>
                            <div v-else class="mc-item__nodelete"></div>
                        </div>
                    </div>

                </div>
                <div v-if="extraMessage" v-html="extraMessage"></div>
            </div>
            <div :class="`mc-foot ${buyNowEnabled ? 'buy-now-enabled' : 'buy-now-disabled'}`" v-if="cartQty > 0">
                <div v-if="buyNowEnabled" class="mc-action mc-checkout">
                    <a href="#" @click.prevent="$emit('buyNowClicked')">
                        <svg v-if="buyNowClicked" style="width: 15px; opacity: 0.5;" version="1.1" id="L7" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" x="0px" y="0px" viewBox="0 0 100 100" enable-background="new 0 0 100 100" xml:space="preserve">
                            <path fill="#000" d="M31.6,3.5C5.9,13.6-6.6,42.7,3.5,68.4c10.1,25.7,39.2,38.3,64.9,28.1l-3.1-7.9c-21.3,8.4-45.4-2-53.8-23.3
                            c-8.4-21.3,2-45.4,23.3-53.8L31.6,3.5z">
                                <animateTransform attributeName="transform" attributeType="XML" type="rotate" dur="2s" from="0 50 50" to="360 50 50" repeatCount="indefinite"></animateTransform>
                            </path>
                            <path fill="#000" d="M42.3,39.6c5.7-4.3,13.9-3.1,18.1,2.7c4.3,5.7,3.1,13.9-2.7,18.1l4.1,5.5c8.8-6.5,10.6-19,4.1-27.7
                            c-6.5-8.8-19-10.6-27.7-4.1L42.3,39.6z">
                                <animateTransform attributeName="transform" attributeType="XML" type="rotate" dur="1s" from="0 50 50" to="-360 50 50" repeatCount="indefinite"></animateTransform>
                            </path>
                            <path fill="#000" d="M82,35.7C74.1,18,53.4,10.1,35.7,18S10.1,46.6,18,64.3l7.6-3.4c-6-13.5,0-29.3,13.5-35.3s29.3,0,35.3,13.5
                            L82,35.7z">
                                <animateTransform attributeName="transform" attributeType="XML" type="rotate" dur="2s" from="0 50 50" to="360 50 50" repeatCount="indefinite"></animateTransform>
                            </path>
                        </svg>
                        <span v-else>Express Checkout</span>
                    </a>
                </div>
                <div class="mc-action mc-cart">
                    <a href="/store/cart.php" class="mc-cartlink">View Cart <svg xmlns="http://www.w3.org/2000/svg" width="8" height="13" fill="none"><path stroke="#FFFFFF" stroke-width="2" d="m.75 1 5.5 5.5L.75 12"/></svg></a>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    props: {
        menuId: {
            default: '',
            type: String,
        },
        open: {
            default: false,
            type: Boolean,
        },
        minicartExclusions: {
            default: '',
            type: String
        },
        buyNowClicked: {
            default: false,
            type: Boolean,
        },
        isMobile: {
            default: false,
            type: Boolean
        },
    },
    watch: {
        cartitems(cartitems) {
            this.$emit('cartItemsSet', cartitems);
        },
    },
    computed: {
        buyNowEnabled() {
            return window._buyNowMiniCartEnabled;
        },
    },
    data() {
        return {
            cartitems: [],
            cartQty: 0,
            storeitems: [],
            loaded: false,
            cartTotal: 0,
            extraClass: '',
            extraMessage: '',
            hover: false,
            timeout: '',
            removeAnnouncementTimeout: null,
            maxChildItemsToShow: 3,
        };
    },
    created(){
        window.addEventListener('scroll', this.hideCart);
    },
    mounted(){
        document.addEventListener('click', (e) => {
            if (e.target.classList.contains('mc-item__remove')) {
                var item = e.target.getAttribute("data-id");
                this.deleteItem(item);
            }
        });
        this.buildMinicart();
    },
    unmounted(){
        window.removeEventListener('scroll', this.hideCart);
    },
    methods: {
        onKeyPress(event) {
            if (event.key === 'Escape') {
                this.$emit('mini-cart-close');
            }
        },
        hideCart() {
            document.querySelector('.mc').style.display=null;
            document.querySelector('.mc').classList.remove('hide');
            clearTimeout(this.timeout);
        },
        formatPrice(value) {
            let formatting_options = {
                style: 'currency',
                currency: 'USD',
                minimumFractionDigits: 2,
            }
            let dollarString = new Intl.NumberFormat("en-US", formatting_options);
            let finalString = dollarString.format(value);
            return(finalString);
        },
        checkImg(url, itemid){
            // Override incorrect or missing product images
            if(itemid.includes('InstallGNutB')){
                return "https://media.sweetwater.com/m/images/store/cart/guitarServiceBannerBone-square.jpg?width=200";
            } else if(itemid.includes('InstallGNutT')){
                return "https://media.sweetwater.com/m/images/store/cart/guitarServiceBannerTusq-square.jpg?width=200";
            } else if(itemid.includes('InstallGS')){
                return "https://media.sweetwater.com/m/images/store/cart/guitarServiceBannerSetup-square.jpg?width=200";
            } else if(itemid.includes('InstallGPlek')){
                return "https://media.sweetwater.com/m/images/store/cart/guitarServiceBannerPlek-square.jpg";
            } else {
                return url;
            }
        },
        checkName(name, itemid){
            // Override incorrect or missing product names
            if (name && name.includes("Description Sweetwater")){
                return name.replace("Description Sweetwater", "");
            } else if (name && name.includes("DescriptionSweetwater")){
                return name.replace("DescriptionSweetwater", "");
            } else if (itemid == 'GiftCard'){
                return "Sweetwater Gift Card";
            } else if (itemid == 'GiftCardE'){
                return "Sweetwater eGift Card";
            } else {
                return name
            }
        },
        checkExclusions(itemid) {
            var excArray = JSON.parse(this.minicartExclusions.replace(/,\s*]$/, ']'));
            for (let i = 0; i < excArray.length; i++) {     
                if(excArray[i].toLowerCase() == itemid.trim().toLowerCase()){
                    return false;          
                }
            }
            return true;
        },
        checkUrl(itemid, serial, detail_page){
            // Override incorrect or missing product URLs
            if(itemid.includes('InstallG')){
                return('/shop/guitars/guitar-repair-shop/');
            }
            if(serial.includes('P:rule')){
                return '#';
            }
            if(detail_page){
                return(detail_page);
            }
            switch(itemid){
                case 'LKRDonation':
                    return('/feature/musicwill/index.php');
                break;
                case 'WestoneCtm':
                    return('/custom/westone/'+serial);
                break;
                case '64AudioCtm':
                    return('/customizer/64audio/'+serial);
                break;
                case 'GiftCardE':
                    return('/shop/gift-cards/email/edit/'+serial);
                break;
                case 'GiftCard':
                    return('/shop/gift-cards/mail/edit/'+serial);
                break;
                default:
                    return('/store/detail/'+itemid);
            }
        },
        checkItemQty(itemId){
            for (let i = 0; i < this.cartitems.length; i++) {
                const item = this.cartitems[i];

                if(itemId == item.itemid){
                    return item.qty;
                }
            }
        },
        checkItemPrice(itemId){
            for (let i = 0; i < this.cartitems.length; i++) {
                const item = this.cartitems[i];

                if(itemId == item.itemid){
                    return item.original_price;
                }
            }
        },
        deleteItem(itemId){
            const cartItem = this.cartitems.find(ci => ci.itemid.toLowerCase() === itemId.toLowerCase());
            let removed = document.getElementById('mc-'+itemId);
            removed.classList.add('mc-removed');
            this.extraClass = 'mc-loading';
            let serialParam = '';
            if (cartItem && cartItem.serial) {
                serialParam = '?serial=' + cartItem.serial;
            }
            axios.delete('/webservices_sw/profile/carts/current/items/' + itemId + '/' + serialParam).then((response) => {
                this.buildMinicart().then(() => {
                    this.$nextTick(() => {
                        const cartToggle = document.querySelector('.site_header__cart');
                        if (cartToggle) {
                            cartToggle.focus();
                        }
                    });
                });
                if (cartItem) {
                    this.$refs.removeAnnouncement.textContent = cartItem.productname + ' removed from cart';
                    if (this.removeAnnouncementTimeout) {
                        clearTimeout(this.removeAnnouncementTimeout);
                    }
                    this.removeAnnouncementTimeout = setTimeout(() => { this.$refs.removeAnnouncement.textContent = ''; }, 1000);
                }
            }).catch((error) => {
                setTimeout(function(){
                    location.reload()
                }, 100);
            });
        },
        revertBuyNow() {
            if (window.location.pathname.includes('/store/checkout')) {
                return Promise.resolve();
            }
            return axios.get('/store/checkout/buy-now/revert/?async=true&from=mini-cart');
        },
        buildMinicart(){
            const wrap = document.getElementById('mini-cart-wrapper');
            return this.revertBuyNow()
                .then(() => {
                    return axios.get(`/webservices_sw/cart/session`)
                        .then((response) => {
                            this.cartitems = response.data.cart.items;
                            this.loaded = true;
                            this.cartTotal = 0;
                            this.cartQty = response.data.cart_quantity;
                            for (let i = 0; i < this.cartitems.length; i++) {
                                var itemTotal = '';
                                if(this.cartitems[i].itemid == 'LKRDonation'){
                                    itemTotal = this.cartitems[i].qty / 100;
                                } else {
                                    itemTotal = this.cartitems[i].qty * this.cartitems[i].price;
                                }
                                if(this.checkItemPrice(this.cartitems[i].itemid) == 88888) {
                                    this.deleteItem(this.cartitems[i].itemid);
                                }
                                if(this.checkExclusions(this.cartitems[i].itemid)){
                                    this.cartTotal += itemTotal;
                                }
                            }
                            var expirationDate = new Date();
                            expirationDate.setDate(expirationDate.getDate() + 7);
                            var currentDomain = "; domain=" + location.hostname;
                            document.cookie = "sws_cartqty=" + this.cartQty + "; expires=" + expirationDate.toUTCString() + currentDomain;
                            this.$emit('cart-items-updated', this.cartQty);
                            this.loaded = true;
                            this.extraClass = '';
                            wrap.style.display = "initial";
                            this.showCartOnPageLoad();
                            
                        }).catch(error => {
                            var cartWrap = document.querySelector('.cart-wrap');
                            cartWrap.classList.remove('mc-mobile-open-animate','mc-mobile-open');
                            document.body.classList.remove('noscroll');
                            this.$emit('cart-items-updated', 0);
                            this.cartitems = [];
                            this.cartTotal = 0;
                            this.cartQty = 0;               
                            this.extraClass = '';
                            this.loaded = true; 
                        });
                });
        },
        showCartOnPageLoad() {
            var status = localStorage.getItem("initLoad");
            var testCheck = localStorage.getItem("showInitCart");
            if (status == null && testCheck && !this.isMobile) {
                document.querySelector('.mc').style.display="block";
                document.querySelector('.cart-wrap').style.position="relative";
                localStorage.setItem("initLoad", "true");
                this.timeout = setTimeout(function() {
                    document.querySelector('.mc').classList.add('hide');
                }, 7000);
            }
        },
        clearTimeout() {
            if (this.timeout) {
                document.querySelector('.mc').classList.remove('hide');
                clearTimeout(this.timeout);
            }
        }
    }
}
</script>


<style rel="stylesheet/scss" lang="scss">

    .hide {
        opacity: 0;
        transition: all .5s ease-out;
    }

    .mc { /* mini-cart */
        background: #fff;
        box-shadow: 0 10px 15px rgba(0,0,0,.1);
        border: 1px solid #E0E0DF;
        display: none;
        position: absolute;
        font-size: 16px;
        top: 100%;
        right: 0;
        width: 350px;
        z-index: 998;

        &.mc--open {
            display: block;
        }

        @media screen and (min-width: 566px) {
            .cart-wrap:hover &,
            &:focus-within {
                display: block;
            }
        }

        &-loader {
            padding: 1em;
            text-align: center;
        }

        &-empty {
            color: #555;
            line-height: 1.4em;
            padding: 1em;
            text-align: center;

            a:link, a:visited {
                color: #0072ba;
            }

            a:hover {
                text-decoration: underline;
            }
        }

        &-title {
            font-weight: 600;
            padding: 1em;
        }

        &-total {
            font-size: 0.9em;
            padding: 0.8em 1em;
        }

        &-cartlink {

            svg {
                margin-left: .25em;
                position: relative;
                top: 1px;
            }

            &:hover {
                background: #edfaff;
            }
        }

        &-qty {
            background: #387D1A;
            border-radius: 50px;
            box-sizing: border-box;
            color: #fff;
            display: inline-block;
            font-size: 14px;
            height: 1.3em;
            margin-left: .25em;
            min-width: 1.3em;
            padding: 1px 4px 0 4px;
            text-align: center;
        }

        &-head,
        &-foot {
            align-items: center;
            display: flex;
            justify-content: space-between;
        }

        &-foot {
            padding: 1em;
        }

        &-items {
            max-height: 300px;
            overflow: auto;
        }

        &-item {
            align-items: center;
            background: #F5F5F5;
            border-bottom: 1px solid #fff;
            display: flex;
            padding: 1em 0 1em 1em;
            position: relative;
            color: #555;
            font-size: .85em;
            justify-content: space-between;
            text-align: left;

            a:hover {
                color: #0072ba;
            }

            &.mc-removed {
                background:rgb(255, 198, 198);
                line-height: 0px;
                max-height: 0px;
                height: 0px;
                padding: 0;
                opacity: 0;
                margin-bottom: -1px;
                transition: .5s;
                overflow: hidden;
            }

            &__nodelete {
                width: 37px;
            }

            &__message {
                background: #ececef;
                padding: 1em;
                font-size: 11px;

                a:link,
                a:visited {
                    color: #0072ba;
                }

                a:hover {
                    text-decoration: underline;
                }
            }
            &__children {
                display: flex;
                gap: 4px;
                margin-top: 0.5rem;

                &-plus-more {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    text-align: center;

                    &:hover {
                        color: initial;
                    }
                }
            }
            &__child-image-wrap {
                background-color: #fff;
                padding: 4px;
                border-radius: 4px;
                width: 35px;
                height: 35px;
                display: flex;
                justify-content: center;
                align-items: center;
            }
            &__child-image {
                max-width: 100%;
                max-height: 100%;
            }
        }

        &-item__gs {
            background: #dfdfdf;
            border-radius: 3px;
            color: #555;
            font-size: 11px;
            padding: 2px;
            width: 100%;
        }

        &-item__qty {
            align-items: center;
            background: #387d1b;
            border-radius: 50px;
            box-sizing: border-box;
            color: #fff;
            display: flex;
            font-size: 12px;
            height: 1.4em;
            justify-content: center;
            left: .6em;
            min-width: 1.4em;
            padding: 0 2px;
            position: absolute;
            top: .6em;
        }
        
        &-item__name {
            flex-direction: column;
            font-weight: 600;
            margin-left: 1em;
            display: flex;
            align-items: flex-start;
            justify-content: center;
            min-height: 40px;
        }

        &-specialoffer {
            background: #e3f3c9;
            color: #357415;
            
            .mc-item__name {
                margin-left: 0;
                min-height: 0;
            }
        }

        &-item__left {
            align-items: flex-start;
            display: flex;
            padding-right: 2em;
        }

        &-item__right {
            align-items: center;
            display: flex;
        }

        &-item__img {
            background-color: #fff;
            box-shadow: 0 0 0 2px #fff;
            border-radius: 1px;
            height: 40px;
            width: 40px;
            object-fit: contain;
        }

        &-item__remove {
            padding: 1em;
            outline-offset: -5px;
            svg {
                pointer-events: none;
            }
            &:focus svg path,
            &:hover svg path{
                stroke: #bd0f15;
            }
        }

        &-item__price {
            text-align: right;
        }

        &-action {
            width: calc(50% - 5px);
        }

        &-action a:link,
        &-action a:visited {
            border-radius: 50px;
            border: solid 2px #387D1A;
            font-weight: 600;
            display: block;
            font-size: .9em;
            outline-offset: 2px;
            padding: .75em;
            text-align: center;
        }

        &-cart a:link,
        &-cart a:visited {
            background: #387D1A;
            color: #fff;

            &:hover {
                filter: brightness(1.1);
            }
        }

        &-checkout a:link,
        &-checkout a:visited {
            background: #fff;
            color: #387D1A;

            &:hover {
                background: #f6f6f6;
            }
        }

    }

    .buy-now-disabled .mc-action {
        width: 100%;
    }

    a:hover.mc-item__name {
        color: #0072ba;
    }

    .circle-spin-2 {
        --size: 16px;
        --stroke-width: calc(var(--size) / 6);
        --accent-opacity: .25;
        --color: currentColor;
        --animation-timing-function: linear;
        --animation-duration: 1s;
        position: relative;
        display: inline-block;
        margin-right: .5em;
        vertical-align: middle;
        width: var(--size);
        height: var(--size);
    }

    @media (prefers-reduced-motion) {
        .circle-spin-2 {
            display: none;
        }
    }

    .circle-spin-2::before,
    .circle-spin-2::after {
        content: '';
        position: absolute;
        inset: 0;
        border-width: var(--stroke-width);
        border-style: solid;
        border-radius: 50%;
        transform: rotate(0deg);
        animation: var(--animation-timing-function) var(--animation-duration) infinite circle-spin-2-animation;
    }

    .circle-spin-2::before {
        border-color: var(--color) var(--color) var(--color) transparent;
        opacity: var(--accent-opacity);
    }

    .circle-spin-2::after {
        border-color: transparent transparent transparent var(--color);
    }

    @keyframes circle-spin-2-animation {
        from {
            transform: rotate(0deg);
        }

        to {
            transform: rotate(360deg);
        }
    }

    .mc-barloader {
        height: 4px;
        background-color: #005c9635;
        width: 100%;
        overflow: hidden;
        display: none;
    }

    .mc-barloader .mc-barloader__progress {
        width: 100%;
        height: 100%;
        background-color: #005c96;
        animation: indeterminateLoader 1s infinite linear;
        transform-origin: 0% 50%;
    }

    @media (prefers-reduced-motion) {
        .mc-barloader .mc-barloader__progress {
            animation: indeterminateLoader 1s linear;
        }
    }

    @keyframes indeterminateLoader {
        0% {
            transform:  translateX(0) scaleX(0);
        }
        40% {
            transform:  translateX(0) scaleX(0.4);
        }
        100% {
            transform:  translateX(100%) scaleX(0.5);
        }
    }

a.mc-item__remove,
a.mc-item__remove:focus-visible {outline-offset: -5px;} //thx safari

.edit-in-cart {
    font-size: 10px;
    text-transform: uppercase;
    font-style: italic;
}

@media screen and (max-width: 566px) {
    .mc-mobile-open {
        background: hsla(34,17%,92%,.5);
        z-index: 1;

        .mc {
            display: block;
            max-height: calc(80dvh - 60px);
            width: 100%;
            opacity: 0;
            transform-origin: top;
            transform: scale(.7);

            &-items {
                max-height: 340px;
            }
        }

        .mobile-mc-overlay {
            background: linear-gradient(to bottom, #0000 60px, #00000075 62px);
            width: 100%;
            height: 100dvh;
            position: absolute;
            top: 0;
            left: 0;
            opacity: 0;
        }
    }

    .mc-mobile-open-animate {
        background: #efefef;

        .mc {
            opacity: 1;
            transform: scale(1);
            transition: .15s;
        }

        .mobile-mc-overlay {
            opacity: 1;
            transition: .15s;
        }
    }
}

.mc-loading {
    .mc-barloader {
        display: block;
    }
    .mc-items {
        opacity: .5;
        pointer-events: none;
    }
}
</style>
